import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ServerPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	customerAccountDetailsTypes,
	customerActivityTypes,
	moneyActivityTypes,
} from 'constants/customer-account.constants';
import { getMoneyActivities, getMoneyActivitiesPagination } from 'state/reducers/customer-accounts.reducers';
import { getCompanyActivities } from 'services/customer-accounts.service';
import _ from 'lodash';
import { SelectList } from 'components/SelectList';
import { activityTypeEnum, generalSelectionEnum } from './tables.types';
import { getCurrencies } from 'state/reducers/global.reducer';
import { ICurrency } from 'constants/business-plan.constants';
import { pagesEnum } from 'constants/ui.constants';
import DateRangePickerInput from 'components/FormCreatorComponent/inputs/DateRangePickerInput';
import { fieldType } from 'components/FormCreator/types';
import { ReportInputFields, formatDate } from 'constants/reports.constants';
import { generalStatusEnum } from 'constants/global.constants';
import { pendingRequestsTypes } from 'constants/requests.constants';
import { PaymentStatus } from 'constants/payment.constants';
import { BalanceStatus, BalanceType, depositType } from 'constants/balance.constants';
import MultipleSelectCheckmarks from 'components/FormElementsComponent/MultipleSelectCheckmarks';
import { Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';
import { useServerPagination } from 'hooks/pagination/useServerPagination';

type IProps = {
	button?: React.ReactNode;
	showFilter?: boolean;
	dataRef?: React.MutableRefObject<{ data: any[] }>;
};

const INITIAL_SORT_BY = 'createdAt';
const INITIAL_SORT_ORDER: Order = DEFAULT_SORT_ORDER;

export const MoneyActivitiesTable: React.FC<IProps> = ({ button, showFilter = false, dataRef }: IProps) => {
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const theme = useTheme();
	const { id } = useParams();
	const moneyActivities = useAppSelector(getMoneyActivities);
	const pagination = useAppSelector(getMoneyActivitiesPagination);
	const currencies = useAppSelector(getCurrencies);
	const [filters, setFilters] = useState<Record<string, any>>({});
	const [activitiesDates, setActivitiesDates] = useState<string>(
		t('page.customerAccounts.tables.moneyActivities.filters.dates.allSelected')
	);

	const filtersPrefix = 'page.customerAccounts.tables.moneyActivities.filters.';

	const activityTypeOptions = [
		{ value: 'Topping Up the Account', name: t(`${filtersPrefix}type.addBalance`) },
		{ value: 'Withdrawal', name: t(`${filtersPrefix}type.BalanceWithdrawal`) },
		{ value: activityTypeEnum.CONVERSION, name: t(`${filtersPrefix}type.Conversion`) },
		{ value: activityTypeEnum.PAYMENT, name: t(`${filtersPrefix}type.Payment`) },
		{ value: activityTypeEnum.PAYMENT_FEE, name: t(`${filtersPrefix}type.PaynentFee`) },
		{ value: activityTypeEnum.BALANCE_REFUND, name: t(`${filtersPrefix}type.BalanceRefund`) },
	];

	// Canonical activity type values for filtering (match backend CTE values)
	const activityTypeFilterMap: Record<string, string> = {
		'Topping Up the Account': 'Topping Up the Account',
		Withdrawal: 'Withdrawal',
		[activityTypeEnum.PAYMENT]: activityTypeEnum.PAYMENT,
		[activityTypeEnum.PAYMENT_FEE]: activityTypeEnum.PAYMENT_FEE,
		[activityTypeEnum.CONVERSION]: activityTypeEnum.CONVERSION,
		[activityTypeEnum.CONVERSION_FEE]: activityTypeEnum.CONVERSION_FEE,
		[activityTypeEnum.BALANCE_REFUND]: activityTypeEnum.BALANCE_REFUND,
		// Alias fallbacks (defensive)
		PaymentFee: activityTypeEnum.PAYMENT_FEE,
		BalanceWithdrawal: 'Withdrawal',
		Balance: 'Topping Up the Account',
	};
	const sortFieldMap: Record<string, string> = {
		createdAt: 'created_at',
		statusHebrew: 'status',
		typeHebrew: 'type',
	};
	const mapSortBy = (sortBy: string) => sortFieldMap[sortBy] || 'created_at';

	const activityStatusOptions = [
		{ value: generalSelectionEnum.ALL, name: t(`${filtersPrefix}status.allSelected`) },
		{ value: generalStatusEnum.APPROVED, name: t(`${filtersPrefix}status.Approved`) },
		{ value: generalStatusEnum.COMPLETED, name: t(`${filtersPrefix}status.Completed`) },
		{ value: generalStatusEnum.CANCELED, name: t(`${filtersPrefix}status.Canceled`) },
		{ value: generalStatusEnum.REJECTED, name: t(`${filtersPrefix}status.Declined`) },
		{ value: generalStatusEnum.PENDING, name: t(`${filtersPrefix}status.Pending`) },
		{ value: generalStatusEnum.PENDINGCCTRANSFER, name: t(`${filtersPrefix}status.PendingCCTransfer`) },
	];

	const [activityStatus, setActivityStatus] = useState(activityStatusOptions[0]);
	const [currency, setCurrency] = useState<any>(null);

	const currencyOptions = useMemo(() => {
		if (currencies?.length > 0) {
			const currenciesList = currencies.map((curr: ICurrency) => ({
				value: curr.id,
				name: `${curr.code} ${curr.symbol}`,
			}));
			currenciesList.unshift({
				value: generalSelectionEnum.ALL,
				name: t(`${filtersPrefix}currency.allSelected`),
			});
			setCurrency(currenciesList[0]);
			return [...currenciesList];
		}
		return [];
	}, [currencies, t]);

	const fetchActivities = useCallback(
		async (
			page: number,
			sortOverride: { sortBy: string; sortOrder: Order },
			currentFilters?: Record<string, any>
		) => {
			if (!id) return;
			await dispatch(
				getCompanyActivities(parseInt(id), {
					offset: page * DEFAULT_PAGE_SIZE,
					limit: DEFAULT_PAGE_SIZE,
					sortBy: mapSortBy(sortOverride.sortBy),
					sortOrder: sortOverride.sortOrder,
					filters: currentFilters,
				})
			);
		},
		[id, dispatch]
	);

	const { currentPage, isLoading, runFetch, handlePageChange, handleSortChange } = useServerPagination<
		Record<string, any> | undefined
	>({
		initialSortBy: INITIAL_SORT_BY,
		initialSortOrder: INITIAL_SORT_ORDER,
		fetchPage: fetchActivities,
	});

	// Transformed data for table
	const tableData = useMemo(() => TableConfig.tableData(moneyActivities), [moneyActivities]);
	// Expose data for Excel export
	useImperativeHandle(dataRef, () => ({ data: tableData }), [tableData]);

	useEffect(() => {
		if (!id) return;
		runFetch(0, undefined, {});
	}, [id, runFetch]);

	const handleFilterChange = (key: string, value: any) => {
		const newFilters = { ...filters };
		if (!value || value === generalSelectionEnum.ALL || (Array.isArray(value) && value.length === 0)) {
			delete newFilters[key];
		} else {
			newFilters[key] = value;
		}
		setFilters(newFilters);
		runFetch(0, undefined, newFilters);
	};

	const handlePageChangeWithFilters = (page: number) => handlePageChange(page, filters);
	const handleSortChangeWithFilters = useCallback(
		(nextSortBy: string, nextSortOrder: Order) => handleSortChange(nextSortBy, nextSortOrder, filters),
		[handleSortChange, filters]
	);

	const handleDateSelection = (event: any) => {
		const dates = {
			startDate: event.startDate,
			endDate: event.endDate.toDateString() === event.startDate.toDateString() ? new Date() : event.endDate,
		};
		setActivitiesDates(
			`${formatDate(dates.startDate)} ${t(`${filtersPrefix}dates.inputMarginLabel`)} ${formatDate(dates.endDate)}`
		);
		handleFilterChange('dateRange', { from: dates.startDate, to: dates.endDate });
	};

	const handleActivityTypeChange = (list: any[]) => {
		const types = list.map((item) => activityTypeFilterMap[item.value] || item.value).filter(Boolean);
		handleFilterChange('types', types);
	};

	const handleActivityStatusChange = (event: any) => {
		setActivityStatus(event.target);
		handleFilterChange('status', event.target.value);
	};

	const handleCurrencyChange = (event: any) => {
		setCurrency(event.target);
		handleFilterChange('currencyId', event.target.value);
	};

	const dateRangeField = {
		type: fieldType.dateRangePicker,
		props: {
			required: false,
			name: ReportInputFields.DATE_RANGE,
			value: activitiesDates,
		},
	};

	const findSourcePayment = (feeId: number) => {
		return moneyActivities.find(
			(activity: any) => activity.type === moneyActivityTypes.PAYMENT && activity.feeId === feeId
		)?.id;
	};

	const chooseNavigationUrl = (item: any) => {
		if ([moneyActivityTypes.PAYMENT, moneyActivityTypes.PAYMENT_FEE].includes(item.type)) {
			return ![
				PaymentStatus.AwaitingAuthorization,
				PaymentStatus.AwaitingFunds,
				PaymentStatus.Completed,
				PaymentStatus.Approved,
				PaymentStatus.Rejected,
				PaymentStatus.Failed,
				PaymentStatus.Pending,
			].includes(item?.status);
		}

		if (item?.status.includes(generalStatusEnum.PENDING) && item.type === moneyActivityTypes.BALANCE_REFUND) {
			return false;
		}

		return item?.status.includes(generalStatusEnum.AWAITING) || item?.status.includes(generalStatusEnum.PENDING);
	};

	const getDefaultUrlType = (itemType: string, item: any) => {
		switch (itemType) {
			case moneyActivityTypes.BALANCE:
				return item && item?.balanceType
					? item?.balanceType === BalanceType.Topping
						? moneyActivityTypes.BALANCE
						: customerAccountDetailsTypes.BALANCE_WITHDRAWAL
					: moneyActivityTypes.BALANCE;
			case moneyActivityTypes.CONVERSION_FEE:
				return moneyActivityTypes.CONVERSION;
			case moneyActivityTypes.BALANCE_REFUND:
				return customerAccountDetailsTypes.BALANCE_REFUND;
			default:
				return item.type;
		}
	};

	const getPendingRequestType = (itemType: string, item: any) => {
		switch (itemType) {
			case moneyActivityTypes.BALANCE:
				return item && item?.balanceType
					? item?.balanceType === BalanceType.Topping
						? pendingRequestsTypes.BALANCES
						: item?.balanceType
					: pendingRequestsTypes.BALANCES;
			case moneyActivityTypes.PAYMENT:
			case moneyActivityTypes.PAYMENT_FEE:
				return pendingRequestsTypes.PAYMENTS;
			default:
				break;
		}
	};

	return (
		<Stack spacing={2} sx={{ position: 'relative' }}>
			{showFilter && (
				<Box sx={{ display: 'flex' }}>
					<Grid container spacing={3} columns={4} sx={{ alignContent: 'center' }}>
						<Grid
							item
							sx={{
								'& .MuiFilledInput-root': {
									borderRadius: '4px!important',
								},
								'& .MuiInputBase-root': {
									height: '52.4px!important',
									'& input': { padding: '0 12px 0 8px!important' },
								},
							}}
						>
							<DateRangePickerInput
								valid={true}
								cField={dateRangeField}
								initialValidation={[]}
								handleChange={undefined}
								sendSelectionToParent={handleDateSelection}
							/>
						</Grid>
						<Grid item sx={{ '& button': { minWidth: 150 } }}>
							<MultipleSelectCheckmarks
								label={t(`${filtersPrefix}type.label`)}
								options={activityTypeOptions}
								handleChange={handleActivityTypeChange}
								primaryLabel={t(`${filtersPrefix}type.allSelected`)}
							/>
						</Grid>
						<Grid item>
							<SelectList
								options={activityStatusOptions}
								selected={activityStatus}
								label={t(`${filtersPrefix}status.label`)}
								handleChange={handleActivityStatusChange}
							/>
						</Grid>
						{currency && (
							<Grid item>
								<SelectList
									options={currencyOptions}
									selected={currency}
									label={t(`${filtersPrefix}currency.label`)}
									handleChange={handleCurrencyChange}
								/>
							</Grid>
						)}
					</Grid>
					{button && <>{button}</>}
				</Box>
			)}

			<Typography variant="subtitle" color={theme.palette.root.grey}>
				{pagination.total + ' ' + t('messages.results')}
			</Typography>
			<ServerPaginatedTable
				rowAction={(row) => {
					const isApprovalPage = chooseNavigationUrl(row);
					const type = getPendingRequestType(row.type, row);
					const defaultUrlType = getDefaultUrlType(row.type, row);
					// Use originalId for conversion rows (backend splits them into buy/sell with composite IDs)
					const rowId = row.originalId || row.id;
					const defaultUrl = `/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${id}/${defaultUrlType}/${rowId}`;
					const approvalUrl = `/${pagesEnum.COMPANY_FEATURE_APPROVE}/${type}/`;
					if (_.isEqual(row.type, moneyActivityTypes.PAYMENT_FEE)) {
						const paymentId = row.paymentId || findSourcePayment(row.feeId);
						isApprovalPage
							? navigate(`${approvalUrl}${paymentId}`)
							: navigate(
									`/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${id}/${moneyActivityTypes.PAYMENT}/${paymentId}`
							  );
					} else if (_.isEqual(row.type, moneyActivityTypes.CONVERSION)) {
						if (row.status !== generalStatusEnum.PENDING) {
							navigate(defaultUrl);
						}
					} else if (_.isEqual(row.type, moneyActivityTypes.CONVERSION_FEE)) {
						if (row.status !== generalStatusEnum.PENDING) {
							const url = `/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${id}/${defaultUrlType}/${row.conversionId}`;
							navigate(url);
						}
					} else if (
						_.includes([BalanceType.Withdrawal], type) &&
						!_.includes([BalanceStatus.Completed, BalanceStatus.Rejected], row?.status)
					) {
						return false;
					} else if (
						_.includes(
							[BalanceType.Withdrawal, BalanceType.Topping, pendingRequestsTypes.BALANCES],
							type
						) &&
						_.includes([BalanceStatus.Completed, BalanceStatus.Rejected], row?.status)
					) {
						navigate(`/${pagesEnum.COMPANY_FEATURE_PROFILE}/${pendingRequestsTypes.BALANCES}/${rowId}`);
					} else {
						navigate(
							isApprovalPage
								? row.depositType === depositType.Digital
									? `/${pagesEnum.COMPANY_FEATURE_PROFILE}/${type}/${rowId}`
									: `${approvalUrl}${rowId}`
								: defaultUrl
						);
					}
				}}
				defaultSortBy={INITIAL_SORT_BY}
				defaultOrderBy={INITIAL_SORT_ORDER}
				data={tableData}
				columns={TableConfig.buildColumns(customerActivityTypes.MONEY_ACTIVITIES, t)}
				perPage={DEFAULT_PAGE_SIZE}
				serverPagination={{
					total: pagination.total,
					hasMore: pagination.hasMore,
					currentPage,
					onPageChange: handlePageChangeWithFilters,
					onSortChange: handleSortChangeWithFilters,
					isLoading,
				}}
			/>
		</Stack>
	);
};
